import React, { useEffect, useState } from 'react';
import { collection, query, orderBy, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebase';

const SnagsPreview = ({ projectId, onClick }) => {
  const [snags, setSnags] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch teams
  useEffect(() => {
    if (!projectId) return;

    const fetchTeams = async () => {
      try {
        const teamsSnapshot = await getDocs(collection(db, `projects/${projectId}/teams`));
        const teamsData = teamsSnapshot.docs.map(doc => ({ 
          id: doc.id, 
          name: doc.data().name,
          color: doc.data().color
        }));
        setTeams(teamsData);
      } catch (error) {
        console.error('Error fetching teams:', error);
      }
    };

    fetchTeams();
  }, [projectId]);

  // Fetch snags
  useEffect(() => {
    if (!projectId) return;

    const unsubscribe = onSnapshot(
      query(
        collection(db, `projects/${projectId}/snags`),
        orderBy('createdAt', 'desc')
      ),
      (snapshot) => {
        if (!snapshot.empty) {
          const snagsData = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              type: 'snag',
              title: data.title,
              description: data.description,
              status: data.status,
              timestamp: data.createdAt?.toDate(),
              category: data.category,
              priority: data.priority,
              assignedTeams: data.assignedTeams || []
            };
          });
          setSnags(snagsData);
        } else {
          setSnags([]);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching snags:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [projectId]);

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const diffInMinutes = Math.floor((now - timestamp) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return '#ef4444';
      case 'high': return '#f97316';
      case 'medium': return '#eab308';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const formatStatus = (status) => {
    if (!status) return 'Unknown';
    // Convert in_progress to "In Progress"
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getTeamNames = (assignedTeamIds) => {
    if (!assignedTeamIds || assignedTeamIds.length === 0) {
      return 'No team assigned';
    }
    
    const teamNames = assignedTeamIds
      .map(teamId => {
        const team = teams.find(t => t.id === teamId);
        return team ? team.name : null;
      })
      .filter(Boolean);
    
    if (teamNames.length === 0) return 'No team assigned';
    if (teamNames.length === 1) return teamNames[0];
    if (teamNames.length === 2) return teamNames.join(', ');
    return `${teamNames.length} teams`;
  };

  if (loading) {
    return (
      <div style={{
        height: '120px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--branding-app-surface-panel-hint)',
        fontSize: '14px'
      }}>
        Loading...
      </div>
    );
  }

  if (snags.length === 0) {
    return (
      <div style={{
        height: '120px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--branding-app-surface-panel-hint)',
        fontSize: '14px'
      }}>
        No recent activity
      </div>
    );
  }

  return (
    <div 
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'transparent',
        padding: '16px',
        transition: 'all 0.2s ease'
      }}
    >
      <div style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        paddingRight: '8px'
      }}>
        {snags.map((snag, index) => {
          const isVariation = snag.category === 'variation';
          
          return (
          <div 
            key={snag.id}
            style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: '12px',
              marginBottom: index < snags.length - 1 ? '16px' : '0',
              paddingBottom: index < snags.length - 1 ? '16px' : '0',
              borderBottom: index < snags.length - 1 ? '1px solid var(--branding-app-surface)' : 'none'
            }}
          >
            {/* Icon */}
            <div style={{
              width: '32px',
              height: '32px',
              background: isVariation ? '#f3e8ff' : '#fef2f2',
              border: isVariation ? '1px solid #c4b5fd' : '1px solid #fecaca',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: '700',
              color: isVariation ? '#7c3aed' : '#dc2626',
              flexShrink: 0
            }}>
              {isVariation ? 'VAR' : 'SNAG'}
            </div>
            
            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--branding-app-surface-panel-fg)',
                marginBottom: '4px',
                lineHeight: '1.3',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {snag.title}
              </div>
              
              <div style={{
                fontSize: '13px',
                color: 'var(--branding-app-surface-panel-sub)',
                marginBottom: '4px',
                lineHeight: '1.3',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {formatStatus(snag.status)} • {getTeamNames(snag.assignedTeams)}
              </div>
              
              {/* Priority and Category badges */}
              <div style={{
                display: 'flex',
                gap: '4px',
                marginBottom: '4px'
              }}>
                <span style={{
                  fontSize: '9px',
                  padding: '2px 6px',
                  borderRadius: '8px',
                  background: getPriorityColor(snag.priority),
                  color: '#fff',
                  fontWeight: '600',
                  textTransform: 'capitalize'
                }}>
                  {snag.priority}
                </span>
                {!isVariation && (
                  <span style={{
                    fontSize: '9px',
                    padding: '2px 6px',
                    borderRadius: '8px',
                    background: 'var(--branding-app-surface)',
                    color: 'var(--branding-app-surface-panel-muted)',
                    fontWeight: '500',
                    textTransform: 'capitalize'
                  }}>
                    {snag.category}
                  </span>
                )}
              </div>
              
              <div style={{
                fontSize: '12px',
                color: 'var(--branding-app-surface-panel-hint)'
              }}>
                {getTimeAgo(snag.timestamp)}
              </div>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default SnagsPreview;
